import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { paymentSession, getRetryPolicyRemaining, selectFrontendBackUrl, selectFrontReturnUrl } from '../../../slices/PaymentSessionDetailsSlice';
import Loading from '../loadingScreen/Loading';
import { useTranslation } from 'react-i18next';
import { getNewUrlWithAllQueryParams, getStorage, isEmpty, isUnresolvedPaymentStatus, updateStorage } from '../../../Utils/utils';
import { getTransactionStatus, selectTransactionData } from '../../../slices/TransactionIdSlice';
import { startAutoRefund } from '../../../slices/AutoRefundSlice';
import PaymentSuccess from '../paymentSuccess/PaymentSuccess';
import { useState } from 'react';

// Total status calls while PENDING: the initial fetch on mount + (this - 1) re-checks.
const PENDING_POLL_MAX_ATTEMPTS = 2;
const PENDING_POLL_INTERVAL_MS = 2000;

const PaymentCompletionHandler = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const backUrl = useSelector((state) => selectFrontendBackUrl(state));
    const returnUrl = useSelector((state) => selectFrontReturnUrl(state));
    const parsedUrl = new URL(window.location.href);
    const params = new URLSearchParams(parsedUrl.search);
    const sessionToken = params.get('sessionToken') || getStorage("session", "tkn");
    const transactionId = params.get('transactionId') || getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
    const transactionData = useSelector((state) => selectTransactionData(state));
    // set when the auto refund loader gave up waiting for a final status of this payment
    const isAutoRefundUnresolved = useSelector((state) => !!transactionId && state.autoRefund.unresolvedTransactionId === transactionId);
    // set while the auto refund loader waits for a final status of this payment
    const isAutoRefundActive = useSelector((state) => !!transactionId && state.autoRefund.activeTransactionId === transactionId);
    const hasTriedAutoRefundRef = useRef(false);
    // auto refund is off or did not start: the page carries on as before
    const [isAutoRefundSkipped, setIsAutoRefundSkipped] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false)
    const isIframePopUp = window.opener && window.opener !== window && window.opener.self !== window.opener.top;
    const pendingPollCount = useRef(0);
    const pendingPollTimer = useRef(null);

    useEffect(() => () => clearTimeout(pendingPollTimer.current), []);

    useEffect(() => {
        if (window.opener) {
            let url = new URL(window.location.href);
            let params = new URLSearchParams(url.search);
            let redirectionResult = params.get('redirectionResult');
            window.opener.postMessage({ redirectionResult, type: 'REDIRECTION_RESULT' }, "*");
        }
    }, [])

    useEffect(() => {
        if (!isEmpty(sessionDetails) && !isEmpty(transactionData)) {
            const frontendReturnUrl = sessionDetails?.paymentDetails?.frontendReturnUrl;
            if (transactionData?.status === "PENDING") {
                // Gateway may report a successful payment as PENDING at first; re-check a couple of
                // times before giving up and sending the user back to checkout (without pns, as it is not a failure).
                if (pendingPollCount.current + 1 < PENDING_POLL_MAX_ATTEMPTS) {
                    pendingPollCount.current += 1;
                    pendingPollTimer.current = setTimeout(() => {
                        dispatch(getTransactionStatus({ token: sessionToken, transactionId }));
                    }, PENDING_POLL_INTERVAL_MS);
                } else if (sessionDetails.callerType === 'PAYMENT_LINK') {
                    return window.location.replace(`/${sessionDetails.token}`);
                } else if (!isIframePopUp) {
                    const checkoutUrl = getNewUrlWithAllQueryParams(location.origin, null, ['redirectionResult']);
                    if (checkoutUrl) {
                        window.location.replace(checkoutUrl);
                    }
                }
            } else if ((transactionData?.status === "FAILED" || transactionData?.status === "Rejected")) {
                if (sessionDetails.callerType === 'PAYMENT_LINK') {
                    return window.location.replace(isAutoRefundUnresolved ? `/${sessionDetails.token}` : `/${sessionDetails.token}?pns=1`);
                    // removed redirection in case of iframe
                } else if (!isIframePopUp) {
                    const urlWithParams = getNewUrlWithAllQueryParams(location.origin, null, ['redirectionResult'], { pns: 1 });
                    if (urlWithParams) {
                        window.location.replace(urlWithParams);
                    }
                }
            } else if (frontendReturnUrl && !window.opener) {
                const returnUrlWithParam = getNewUrlWithAllQueryParams(`${window.location.origin}/finalStatus${location.search}`, null, 'token');
                // removed redirection in case of iframe
                if (!isIframePopUp && returnUrlWithParam && location.pathname !== '/finalStatus') {
                    window.location.replace(`/finalStatus${location.search}`);
                }
            } else {
                // removed redirection in case of iframe and not popup window
                if (!(isIframePopUp || window.opener)) {
                    window.location.replace('/finalStatus');
                }
            }
        }
    }, [sessionDetails, transactionData, isAutoRefundUnresolved, isAutoRefundActive, isAutoRefundSkipped])

    useEffect(() => {
        if (!isEmpty(sessionToken) && !isEmpty(transactionId)) {
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const sessionTokenFromStorage = getStorage("session", "tkn");
            const transactionIdFromStorage = getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
            if (isEmpty(sessionTokenFromStorage) && isEmpty(transactionIdFromStorage)) {
                updateStorage("session", "clear");
            }
            // non seemless flow

            // return
            updateStorage("session", "update", "tkn", sessionToken);
            updateStorage("session", "update", "t_id", transactionId);
            if (transactionData?.status == "PAID" && sessionDetails?.paymentDetails?.frontendReturnUrl) {
                setShowSuccessPage(true)
            }
            dispatch(paymentSession({ token: sessionToken, browserTimeZone }));
            dispatch(getRetryPolicyRemaining({ token: sessionToken }));
            dispatch(getTransactionStatus({ token: sessionToken, transactionId: transactionId }));
        } else {
            if (isIframePopUp) {
                // window.close()
            } else {
                const newUrl = getNewUrlWithAllQueryParams(returnUrl ? returnUrl : backUrl, null, 'token');
                if (newUrl) {
                    window.location.href = newUrl;
                }
            }
        }
    }, []);

    return (
        <>
            {showSuccessPage ? <PaymentSuccess /> : <Loading
                text={`${t("LOADING")}...`}
                subText=""
            />}
        </>
    )
}

export default PaymentCompletionHandler